/**
 * Case List Page - Modern Design
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, Eye, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCases, useLawyers } from '@/hooks/useCases'
import { CASE_STATUS_LABELS, CASE_STATUS_COLORS } from '@/types/case'

export function CaseList() {
  // State for filters and pagination
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [status, setStatus] = useState<string>('')
  const [lawyerFilter, setLawyerFilter] = useState<string>('')

  // Fetch lawyers for filter dropdown
  const { data: lawyers } = useLawyers()

  // Fetch cases with filters
  const { data, isLoading, error, refetch } = useCases(
    {
      search,
      status: status || undefined,
      user_id: lawyerFilter || undefined,
    },
    { page, limit }
  )

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchInput.trim()) {
      setSearch(searchInput.trim())
      setPage(1)
    }
  }

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    setStatus(value)
    setPage(1)
  }

  // Handle lawyer filter change
  const handleLawyerChange = (value: string) => {
    setLawyerFilter(value)
    setPage(1)
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Cases</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Briefcase className="h-8 w-8" />
            Cases
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and track all legal cases
          </p>
        </div>
      </div>

      {/* Search and Filters Card */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <form onSubmit={handleSearch}>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by case number or title..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Lawyer Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
              <select
                value={lawyerFilter}
                onChange={(e) => handleLawyerChange(e.target.value)}
                className="appearance-none pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer bg-white min-w-[200px]"
              >
                <option value="">All Lawyers</option>
                {lawyers?.map((lawyer) => (
                  <option key={lawyer.userid} value={lawyer.userid}>
                    {lawyer.fullname}
                  </option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 rotate-90 pointer-events-none" />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
              <select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="appearance-none pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer bg-white min-w-[200px]"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="ongoing">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
                <option value="archived">Archived</option>
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 rotate-90 pointer-events-none" />
            </div>

            {/* Search Button */}
            <button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              Search
            </button>

            {/* Clear Button */}
            {(search || status || lawyerFilter) && (
              <button
                type="button"
                onClick={() => {
                  setSearch('')
                  setSearchInput('')
                  setStatus('')
                  setLawyerFilter('')
                  setPage(1)
                }}
                className="border border-gray-300 hover:bg-gray-50 px-6 py-3 rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Active Filters */}
        {(search || status || lawyerFilter) && (
          <div className="mt-4 pt-4 border-t flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Active filters:</span>
            {search && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                Search: "{search}"
              </span>
            )}
            {lawyerFilter && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                Lawyer: {lawyers?.find(l => l.userid === lawyerFilter)?.fullname}
              </span>
            )}
            {status && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                Status: {CASE_STATUS_LABELS[status]}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Summary */}
      {data && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{data.items.length}</span> of{' '}
            <span className="font-semibold text-gray-900">{data.total}</span> cases
          </p>
          {data.pages > 1 && (
            <p className="text-gray-600">
              Page <span className="font-semibold text-gray-900">{page}</span> of{' '}
              <span className="font-semibold text-gray-900">{data.pages}</span>
            </p>
          )}
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-gray-600">Loading cases...</p>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No cases found</h3>
            <p className="text-gray-600 mb-6">
              {search || status
                ? 'Try adjusting your search or filters'
                : 'Cases are created automatically from the visit wizard'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Case Number
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Case Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Court Code
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Lawyer
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.items.map((caseItem) => (
                    <tr
                      key={caseItem.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {caseItem.case_number || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {caseItem.title || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {caseItem.court_code || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {caseItem.lawyer_firstname && caseItem.lawyer_lastname ? (
                          <span>Atty. {caseItem.lawyer_firstname} {caseItem.lawyer_lastname}</span>
                        ) : caseItem.lawyer_username ? (
                          <span>{caseItem.lawyer_username}</span>
                        ) : (
                          <span className="text-gray-400">Not Assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* View Button */}
                          <Link to={`/cases/${caseItem.id}`}>
                            <button
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium transition-colors"
                              title="View Case"
                            >
                              <Eye className="w-4 h-4" />
                              <span className="text-sm">View</span>
                            </button>
                          </Link>

                          {/* Edit Button hidden for now */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.pages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {page} of {data.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                    className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(data.pages, p + 1))}
                    disabled={page === data.pages || isLoading}
                    className="inline-flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CaseList
